import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Eye, Edit, Trash2, Plus, Loader2 } from "lucide-react";
import { usePolicyVersions, useDeletePolicyVersion, usePolicyVersionStats, useUpdatePolicyVersion, usePublishPolicyVersion } from "@/hooks/superadmin/usePolicyVersions";
import { PolicyVersionEditor } from "./PolicyVersionEditor";
import { PolicyVersionPreview } from "./PolicyVersionPreview";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { isMarkdown, markdownToHtml } from "@/utils/markdownToHtml";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Database } from "@/integrations/supabase/types";

type PolicyVersion = Database["public"]["Tables"]["policy_versions"]["Row"];

const POLICY_TYPE_LABELS: Record<string, string> = {
  privacy_policy: "Privacy Policy",
  terms_acceptance: "Terms of Service",
  cookies: "Cookie Policy",
  marketing: "Marketing Consent",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 border-gray-200",
  active: "bg-green-100 text-green-800 border-green-200",
  archived: "bg-yellow-100 text-yellow-800 border-yellow-200",
};

export default function PolicyVersions() {
  const [selectedPolicyType, setSelectedPolicyType] = useState<"privacy_policy" | "terms_acceptance" | "cookies" | "marketing">("privacy_policy");
  const [viewTab, setViewTab] = useState<"preview" | "edit">("preview");
  const [editingVersion, setEditingVersion] = useState<PolicyVersion | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<PolicyVersion | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<PolicyVersion | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editEffectiveDate, setEditEffectiveDate] = useState("");
  const { toast } = useToast();
  const deleteMutation = useDeletePolicyVersion();
  const updateMutation = useUpdatePolicyVersion();
  const publishMutation = usePublishPolicyVersion();

  const { data: versions, isLoading, error, refetch } = usePolicyVersions({
    policyType: selectedPolicyType,
  });

  const { data: stats } = usePolicyVersionStats({
    policyType: selectedPolicyType,
  });

  const getConsentCount = (version: string) => {
    return stats?.find((s) => s.version === version)?.consent_count || 0;
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDelete = async () => {
    if (!deletingVersion) return;

    try {
      await deleteMutation.mutateAsync(deletingVersion.id);
      toast({
        title: "Success",
        description: "Policy version deleted successfully",
      });
      setDeletingVersion(null);
      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete policy version",
        variant: "destructive",
      });
    }
  };


  const activeVersion = versions?.find((v) => v.status === "active") || versions?.[0] || null;
  const history = versions?.filter((v) => v.id !== activeVersion?.id) || [];

  // Convert Markdown to HTML for preview display
  const previewContent = useMemo(() => {
    const content = activeVersion?.content || "";
    if (!content) return "";
    // Convert Markdown to HTML if needed for backward compatibility
    return isMarkdown(content) ? markdownToHtml(content) : content;
  }, [activeVersion?.content]);

  // Sync edit fields with active version
  React.useEffect(() => {
    if (activeVersion) {
      // Convert Markdown to HTML if needed for backward compatibility
      let content = activeVersion.content || "";
      if (isMarkdown(content)) {
        content = markdownToHtml(content);
      }
      setEditContent(content);
      setEditEffectiveDate(
        activeVersion.effective_date
          ? new Date(activeVersion.effective_date).toISOString().split("T")[0]
          : ""
      );
    } else {
      setEditContent("");
      setEditEffectiveDate("");
    }
  }, [activeVersion]);

  const handleSaveEdit = async () => {
    if (!activeVersion) return;
    try {
      await updateMutation.mutateAsync({
        id: activeVersion.id,
        data: {
          content: editContent,
          effective_date: editEffectiveDate || null,
        },
      });
      toast({ title: "Saved", description: "Policy content updated." });
      await refetch();
      setViewTab("preview");
    } catch (e: unknown) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Failed to save policy",
        variant: "destructive",
      });
    }
  };

  const handlePublish = async (): Promise<void> => {
    if (!activeVersion) return;
    try {
      await publishMutation.mutateAsync(activeVersion.id);
      toast({ title: "Published", description: "Policy version published." });
      await refetch();
      setViewTab("preview");
    } catch (e: unknown) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Failed to publish policy",
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground p-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading policy versions...
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6 text-red-600">
          Error loading policy versions: {error.message}
        </div>
      );
    }

    if (!versions || versions.length === 0) {
      return (
        <div className="p-6 text-muted-foreground">
          No policy versions found. Create one to get started.
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 min-h-0">
        {/* Current document */}
        <div className="rounded-lg border bg-white p-4 shadow-sm flex flex-col min-h-0">
          <div className="flex flex-col gap-2 border-b pb-3 mb-3">
            <div className="flex flex-col gap-3">
              <h3 className="text-lg font-semibold">
                Document Editor
              </h3>

              <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-xs  tracking-wide text-muted-foreground">Current version</span>
                    <span className="text-xs bg-white border rounded-full p-1 px-2">
                      {activeVersion?.version || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs tracking-wide text-muted-foreground">Effective Date</span>
                    <span className="text-xs bg-white border rounded-full px-2 py-1">
                      {activeVersion?.effective_date ? formatDate(activeVersion.effective_date) : "Not set"}
                    </span>
                  </div>
                </div>
                <Badge variant="outline">
                  {activeVersion?.status ? STATUS_LABELS[activeVersion.status] : "—"}
                </Badge>
              </div>
              
            </div>

            <div className="mt-2">
              <Tabs value={viewTab} className="rounded-xl" onValueChange={(v) => setViewTab(v as any)}>
                <TabsList className="grid grid-cols-2 w-fit">
                  <TabsTrigger className="text-xs rounded-lg" value="preview">Preview</TabsTrigger>
                  <TabsTrigger className="text-xs rounded-lg" value="edit" disabled={!activeVersion}>Edit</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {viewTab === "preview" && (
            <div className="rounded-md border bg-muted/20 p-4">
              <RichTextDisplay
                content={previewContent}
                className="prose prose-sm max-w-none"
                fallbackText="No content available for this version."
              />
            </div>
          )}

          {viewTab === "edit" && activeVersion && (
            <div className="rounded-md border bg-muted/20 p-4 flex flex-col gap-4">
              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Effective Date</span>
                  <input
                    type="date"
                    className="text-xs border rounded px-3 py-2 bg-white"
                    value={editEffectiveDate}
                    onChange={(e) => setEditEffectiveDate(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handlePublish}
                    disabled={publishMutation.isPending}
                  >
                    {publishMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      "Publish"
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Content</div>
                <RichTextEditor
                  value={editContent}
                  onChange={(html) => setEditContent(html)}
                  placeholder="Enter your policy document content here. Use the toolbar to format text with headings, bold, italic, lists, and links."
                />
                <p className="text-xs text-muted-foreground">
                  Use the formatting toolbar to style your content. Existing Markdown content will be automatically converted to Rich Text format.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Version history */}
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-3">
            <h4 className="text-base font-semibold">Version History</h4>
            <p className="text-sm text-muted-foreground">
              Last published versions for this policy. Click preview to view details.
            </p>
          </div>

          {history.length === 0 && !activeVersion ? (
            <div className="text-sm text-muted-foreground">No versions available.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/70 text-muted-foreground border-b">
                    <th className="text-left px-4 py-2 font-medium">Version</th>
                    <th className="text-left px-4 py-2 font-medium">Effective Date</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Created</th>
                    <th className="text-left px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[activeVersion, ...history].filter(Boolean).map((version) => (
                    <tr key={version!.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <span className="font-medium">{version!.version}</span>
                      </td>
                      <td className="px-4 py-3">{formatDate(version!.effective_date)}</td>
                      <td className="px-4 py-3">
                        <Badge className={STATUS_COLORS[version!.status]}>
                          {STATUS_LABELS[version!.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">{formatDate(version!.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewVersion(version!)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingVersion(version!);
                              setIsCreating(false);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {version!.status !== "active" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeletingVersion(version!)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      <Tabs
        value={selectedPolicyType}
        onValueChange={(value) => setSelectedPolicyType(value as typeof selectedPolicyType)}
        className="flex flex-col min-h-0"
      >
        <TabsList className="grid grid-cols-4 w-fit text-xs rounded-xl">
          <TabsTrigger
          className="text-xs rounded-lg" value="privacy_policy">Privacy Policy</TabsTrigger>
          <TabsTrigger className="text-xs rounded-lg" value="terms_acceptance">Terms of Service</TabsTrigger>
          <TabsTrigger className="text-xs rounded-lg" value="cookies">Cookie Policy</TabsTrigger>
          <TabsTrigger className="text-xs rounded-lg" value="marketing">Marketing</TabsTrigger>
        </TabsList>

        <TabsContent value={selectedPolicyType} className="mt-4 flex-1 min-h-0">
          {renderContent()}
        </TabsContent>
      </Tabs>

      {/* Editor Dialog */}
      <PolicyVersionEditor
        isOpen={editingVersion !== null || isCreating}
        onClose={() => {
          setEditingVersion(null);
          setIsCreating(false);
        }}
        version={isCreating ? null : editingVersion}
        policyType={selectedPolicyType}
        onSave={() => {
          refetch();
        }}
      />

      {/* Preview Dialog */}
      <PolicyVersionPreview
        isOpen={previewVersion !== null}
        onClose={() => setPreviewVersion(null)}
        version={previewVersion}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletingVersion !== null} onOpenChange={(open) => !open && setDeletingVersion(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy Version</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete version "{deletingVersion?.version}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
